import React, { useState, useEffect } from 'react';
import { useIntl, history } from 'umi';
import { Button, Bubble, TableColumn, PagingQuery } from 'tea-component';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import ColumnsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getCompanyDetailsPath } from '@/pages/intelligence/utils/topchartsUtils';
import { getIPOStatusesNamesMapping } from '@/pages/intelligence/components/TeaSelector/IPOStatusSelector';
import {
  CompaniesMetricColumn,
  getRevenueTableList,
  PAGE_COLUMNS_METADATAS_MAPPING,
  PAGE_COLUMNS_METADATAS_MAPPINGS,
} from './utils';
import { CBSvgIconTips } from './utils/Tips';
import CBIcon from '@/pages/intelligence/assets/svg/dataSource/crunchbase.svg';
import type { IGameType } from '@/pages/intelligence/types/common';
import type { TopchartTableProps } from '@/pages/intelligence/pages/_config/topchartsInterface';
import type { ITopChartsCompaniesBaseProps, ITopChartsCompaniesRank } from '../../types';

const DEFAULT_PAGE_SIZE = 100;

interface ICompaniesTablesProps extends TopchartTableProps, ITopChartsCompaniesBaseProps {
  entityType: 'company' | string;
  gameTypes?: IGameType[];
}

export default function CompaniesTables(props: ICompaniesTablesProps) {
  const {
    loading,
    entityType,
    topChartsType,
    gameTypes = ['mobile', 'pc', 'console'] as IGameType[],
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
  } = props;

  const { formatMessage } = useIntl();

  const {
    tablePageIndex,
    tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
    dataSources,
  } = pageSortAttr || {};
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || DEFAULT_PAGE_SIZE);
  const [selectList, setSelectList] = useState(tableSelection);

  const ipoStatusesNamesMapping = getIPOStatusesNamesMapping();

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || DEFAULT_PAGE_SIZE);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricCloums = (): TableColumn<ITopChartsCompaniesRank>[] => {
    const metricCloums: any[] = [];
    let titles =
      PAGE_COLUMNS_METADATAS_MAPPINGS(
        dataSources[0] === 'company_sensortower' ? 'sensortower' : 'appannie',
      )[topChartsType] || [];
    if (topChartsType === 'top_companies_revenue') {
      titles = getRevenueTableList(gameTypes);
    }

    titles.forEach((item: any) => {
      if (item.key === 'mobile_revenue' || item.key === 'downloads') {
        item.dataSources = [dataSources[0] === 'company_sensortower' ? 'sensortower' : 'appannie'];
      }
      const metricKey = item?.key;
      metricCloums.push(
        CompaniesMetricColumn({
          topChartsType,
          dataSources: item?.dataSources,
          dataSource: item?.dataSource,
          key: metricKey,
          title: item?.title,
          // title已配置为中英文翻译的key，不需要title_cn这个字段了，加上是为了阻止ts报错
          title_cn: '',
          content: item?.content,
          tipText: item?.tipText,
          ratioSortable: item?.ratioSortable,
          sortMetric: metricKey,
          currentSortKey: `${currentSortMetric}_${currentRatioType}`,
          currentSortValue,
          onSortChage,
          entityType: item?.entityType || '',
          metricsource: item?.source || '',
          mertricKey: item?.mertricKey || '',
        }),
      );
    });
    return metricCloums;
  };

  const handleClickCompany = (companyId: string) => {
    history.push(getCompanyDetailsPath({ companyId, tab: 'Summary', dataSources: dataSources[0] }));
  };

  /**
   * 这个方法展示没用上，目前不管勾选什么类型，都展示所有类型的游戏数量
   * 获取公司游戏数量
   * 非revenue榜单，返回所有类型游戏数量
   * revenue榜单，需要根据筛选器勾选的游戏类型，计算对应的数量
   * @param companyRank 公司rank数据对象
   */
  // const getGamesNum = (companyRank: ITopChartsCompaniesRank): number => {
  //   // mobile、pc、console 所有游戏数量
  //   const totalNum: number = companyRank?.game_cnt || 0;
  //   // 非revenue榜单时，直接返回所有类型游戏数量
  //   if (topChartsType !== 'top_companies_revenue') return totalNum;
  //   // revenue榜单 异常情况时，返回所有游戏数量
  //   if (!Array.isArray(gameTypes)) return totalNum;
  //   const gameNumKey: {
  //     [key in IGameType]: string;
  //   } = {
  //     mobile: 'mo_cnt',
  //     pc: 'pc_cnt',
  //     console: 'co_cnt',
  //   };
  //   // 计算勾选的游戏类型 对应的 游戏数量总和
  //   let num = 0;
  //   for (const gameType of gameTypes) {
  //     const numKey = gameNumKey[gameType];
  //     if (!numKey) continue;
  //     // @ts-ignore
  //     const numItem = companyRank?.[numKey];
  //     if (typeof numItem === 'number' && !Number.isNaN(numItem)) {
  //       num = num + numItem;
  //     }
  //   }
  //   return num;
  // };

  return (
    <>
      <TopchartTable
        loading={loading}
        recordKey="app_id"
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={
          [
            {
              key: 'select',
              fixed: 'left',
              width: 36,
              header: (
                <TableCheckboxHeader
                  disabled={loading || records?.length === 0}
                  tableSelectionAll={tableSelectionAll}
                  selectList={selectList}
                  onChange={(list, stats) => {
                    onTableCheckGame(list, stats);
                    setSelectList(list);
                  }}
                />
              ),
              render: (cvm) => (
                <TableCheckboxRender
                  cvm={{ ...(cvm || {}), gameId: cvm?.app_id, color: '' }}
                  selectList={selectList}
                  onChange={(list, status) => {
                    setSelectList(list);
                    onTableCheckGame(list, status);
                  }}
                />
              ),
            },
            {
              key: 'companies',
              fixed: 'left',
              width: 330,
              header: (
                <div className="dbi-companies-table-header-other dbi-firstRow-flex">
                  <span className="dbi-companies-table-flex-id dbi-text-id">#</span>
                  <Bubble
                    style={{ maxWidth: 500 }}
                    content={<CBSvgIconTips source={'Crunchbase'} />}
                  >
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img className="dbi-companies-table-header-icon" src={CBIcon} />
                      <span>{formatMessage({ id: 'dbi_company_companies' })}</span>
                    </div>
                  </Bubble>
                </div>
              ),
              render: (cvm) => {
                const companyId = cvm?.app_id;
                return (
                  <FirstRow
                    id={cvm?.rank}
                    cvm={{
                      gametitle: cvm?.name,
                      imgType: entityType,
                      img: cvm?.image_url,
                      countryIcon: getCountryFlag(cvm?.location_identifiers_country_code),
                      text: [cvm?.location_identifiers_city, cvm?.location_identifiers_country]
                        .filter((item) => item?.length)
                        .join(', '),
                    }}
                    rihgtClickToJumpHref={getCompanyDetailsPath({ companyId, tab: 'Summary' })}
                    onClick={() => {
                      handleClickCompany(companyId);
                    }}
                  />
                );
              },
            },
            {
              key: 'gameOwned',
              width: 150,
              align: 'right',
              header: (
                <ColumnsCenter width={150} align="right">
                  <div className="dbi-companies-table-header-end">
                    {formatMessage({ id: 'dbi_company_game_owned' })}
                  </div>
                </ColumnsCenter>
              ),
              render: (cvm) => (
                <ColumnsCenter width={150} align="right">
                  {/* {getGamesNum(cvm)} */}
                  {cvm?.game_cnt}
                </ColumnsCenter>
              ),
            },
            {
              key: 'listed',
              width: 150,
              align: 'right',
              header: (
                <ColumnsCenter width={150} align="right">
                  <div className="dbi-companies-table-header-end">
                    <Bubble
                      style={{ maxWidth: 500 }}
                      content={<CBSvgIconTips source={'Crunchbase'} />}
                    >
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <img className="dbi-companies-table-header-icon" src={CBIcon} />
                        {formatMessage({ id: 'dbi_company_listed' })}
                      </div>
                    </Bubble>
                  </div>
                </ColumnsCenter>
              ),
              render: (cvm) => (
                <ColumnsCenter width={150} align="right">
                  {ipoStatusesNamesMapping?.[cvm?.ipo_status]}
                </ColumnsCenter>
              ),
            },
            ...getMetricCloums(),
            {
              key: 'actions',
              header: formatMessage({ id: 'dbi_actions' }),
              align: 'right',
              width: 100,
              render: (cvm) => (
                <Button
                  className="table-link"
                  type="link"
                  onClick={() => {
                    handleClickCompany(cvm?.app_id);
                  }}
                >
                  {formatMessage({ id: 'dbi_details' })}
                </Button>
              ),
            },
          ] as TableColumn<ITopChartsCompaniesRank>[]
        }
      />
    </>
  );
}
